import React, { useContext, useEffect, useRef, useState } from 'react';
import { Translate } from 'react-localize-redux';

import { Link, useLocation } from 'react-router-dom';
import {
  Button,
  Collapse,
  Container,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  Navbar,
  NavbarBrand,
  NavItem,
  NavLink,
  UncontrolledDropdown
} from 'reactstrap';
import cmp from 'semver-compare';
import { useBoolean, useOnClickOutside } from 'usehooks-ts';
import logotypeGolfamoreBlack from '../../assets/img/logo-golfamore-black.png';

import logotypeGolfamoreWhite from '../../assets/img/logo-golfamore-white.png';
import getPath from '../../assets/routes/paths';

import * as api from '../../services/api';
import getLanguage from '../../services/helpers';
import { Store } from '../../Store';
import Search from '../Search/Search';

import TopBanner from '../TopBanner';
import styles from './Navbar.module.scss';
import { getGolfCoursesMenuItems } from './utils';

function MainNavbar() {
  const menuRef = useRef(null);
  const location = useLocation();
  const { value: isMenuVisible, toggle: toggleMenu, setFalse: hideMenu } = useBoolean(false);
  const [showCampaignBanner, setShowCampaignBanner] = useState(false);
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);

  const languageCode = getLanguage();

  useEffect(hideMenu, [hideMenu, location]);

  useEffect(() => {
    (async () => {
      const { response } = await api.getCurrentCampaign();
      if (response?.bannerText) {
        setShowCampaignBanner({ ...response, show: true });
      }
      // else {
      // setShowCampaignBanner({ show: true });
      // }
    })();
  }, []);

  useEffect(() => {
    setIsSearchDropdownOpen(false);
  }, [location]);

  useOnClickOutside(menuRef, hideMenu);

  return (
    <div ref={menuRef} className="sticky-top">
      <Navbar className="align-items-stretch flex-column" id="navbar-main" expand="lg">
        {showCampaignBanner?.show && (
          <TopBanner text={showCampaignBanner.bannerText} expandedText={showCampaignBanner.bannerTextExpanded} />
        )}
        <div
          className="d-flex w-100 align-items-stretch justify-content-between justify-content-lg-center flex-1 py-1 pr-3"
          style={{ paddingLeft: 'calc((100vw - 1140px)/2)' }}
        >
          <Button
            onClick={toggleMenu}
            className={`bg-transparent d-flex d-lg-none justify-content-center ml-3 navbar-nav ${styles.toggler}`}
          >
            <span className="nav-item">
              <span className="nav-link p-0">
                <i className={`fa d-block ${isMenuVisible ? 'fa-times' : 'fa-bars'} m-0`} style={{ fontSize: 24, right: 0, top: 0 }} />
              </span>
            </span>
          </Button>
          <NavbarBrand id="navbar-brand" to={`/${languageCode}/`} tag={Link}>
            <img src={logotypeGolfamoreWhite} id="gh-logotype-white" alt="Golfamore logo" />
            <img src={logotypeGolfamoreBlack} id="gh-logotype-black" alt="Golfamore logo" className="d-none" />
          </NavbarBrand>

          <div className="d-flex flex-lg-grow-1 align-items-stretch mr-3">
            <MenuMainItems className={`d-none d-lg-flex ${styles.desktopMenu}`} />
            <Nav navbar className={`d-flex ml-auto align-items-stretch justify-content-center ${styles.desktopMenu}`}>
              <MenuSecondaryItems className="d-none d-lg-block" />

              <Dropdown
                nav
                inNavbar
                className="mx-2 d-none d-lg-flex align-items-center"
                isOpen={isSearchDropdownOpen}
                toggle={() => setIsSearchDropdownOpen(!isSearchDropdownOpen)}
              >
                <DropdownToggle color="default" nav>
                  <i className="fa fa-search" style={{ fontSize: 24 }} />
                </DropdownToggle>
                <DropdownMenu right className={`dropdown ${styles.dropdownMenu}`} style={{ minWidth: '360px' }}>
                  <Search keyValue={location.key} inputProps={{ style: { borderRadius: 0, border: 0, padding: '26px 20px' } }} />
                </DropdownMenu>
              </Dropdown>

              <MyNavItem to={getPath(languageCode, 'Webshop')} tag={Link} className="link-button nav-btn">
                <span className="d-none d-sm-inline">
                  <Translate id="navbar.buy" />
                </span>
                <i className="fa fa-shopping-cart d-block" style={{ fontSize: 24 }} />
              </MyNavItem>
            </Nav>
          </div>
        </div>
        <Collapse isOpen={isMenuVisible} className="d-lg-none">
          <Container className="py-4">
            <div>
              <Search keyValue={location.key} />
            </div>
            <MenuMainItems className={`mx-0 my-3 ${styles.mobileMenu}`} />
            <Nav navbar className="text-align-center">
              <MenuSecondaryItems />
            </Nav>
          </Container>
        </Collapse>
      </Navbar>
    </div>
  );
}

export default MainNavbar;

const MyNavItem = ({ children, className, ...props }) => {
  const { state } = React.useContext(Store);

  const handleClick = (event) => {
    if (process.env.NODE_ENV === 'development') return;
    if (state.settings.versionAvailable && cmp(state.settings.versionAvailable, process.env.REACT_APP_VERSION)) {
      if (navigator.onLine) {
        event.preventDefault();
        window.location.href = props.to;
      }
    }
  };

  return (
    <NavItem className={`d-lg-flex align-items-center ${className || ''}`}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <NavLink {...props} onClick={handleClick}>
        {children}
      </NavLink>
    </NavItem>
  );
};

const MenuMainItems = (props) => {
  const languageCode = getLanguage();
  const courseLink = getPath(languageCode, 'GolfCourses');

  const coursesMenuItems = getGolfCoursesMenuItems();

  return (
    <Nav navbar {...props}>
      <UncontrolledDropdown nav inNavbar className="d-lg-flex align-items-center">
        <DropdownToggle color="default" caret nav>
          <Translate id="navbar.card.title" />
        </DropdownToggle>
        <DropdownMenu className={`dropdown ${styles.dropdownMenu}`}>
          <DropdownItem to={getPath(languageCode, 'Webshop')} tag={Link} className="not-default">
            <Translate id="navbar.card.submenu.webshop" />
          </DropdownItem>
          <DropdownItem to={getPath(languageCode, 'WhyBuyCard')} tag={Link} className="not-default">
            <Translate id="navbar.card.submenu.why-buy-card" />
          </DropdownItem>
          <DropdownItem to={getPath(languageCode, 'HowItWorks')} tag={Link} className="not-default">
            <Translate id="navbar.card.submenu.how-it-works" />
          </DropdownItem>
          <DropdownItem to={getPath(languageCode, 'Activate')} tag={Link} className="not-default">
            <Translate id="navbar.card.submenu.activate" />
          </DropdownItem>
        </DropdownMenu>
      </UncontrolledDropdown>

      <UncontrolledDropdown nav inNavbar className="d-lg-flex align-items-center">
        <DropdownToggle color="default" caret nav>
          <Translate id="navbar.golf-courses.title" />
        </DropdownToggle>
        <DropdownMenu className={`dropdown ${styles.dropdownMenu}`}>
          {!!coursesMenuItems &&
            coursesMenuItems.map((item) => (
              <DropdownItem key={item.link} to={item.link} tag={Link} className="not-default">
                {item.text}
              </DropdownItem>
            ))}
          <DropdownItem to={`${courseLink}`} tag={Link} className="not-default">
            <Translate id="default.show-courses" />
          </DropdownItem>
        </DropdownMenu>
      </UncontrolledDropdown>

      {'sv' === languageCode && (
        <UncontrolledDropdown nav inNavbar className="d-lg-flex align-items-center">
          <DropdownToggle nav caret>
            <Translate id="navbar.events.title" />
          </DropdownToggle>
          <DropdownMenu className={`dropdown ${styles.dropdownMenu}`}>
            <DropdownItem tag={Link} className="not-default" to={getPath(languageCode, 'Events')}>
              <Translate id="navbar.events.submenu.all-events" />
            </DropdownItem>
            <DropdownItem tag={Link} className="not-default" to={getPath(languageCode, 'Competitions')}>
              <Translate id="navbar.events.submenu.competitions" />
            </DropdownItem>
            <DropdownItem tag={Link} className="not-default" to={getPath(languageCode, 'CompetitionGolfamoreTrophy')}>
              <Translate id="navbar.events.submenu.gt" />
            </DropdownItem>
            <DropdownItem tag={Link} className="not-default" to={getPath(languageCode, 'CompetitionUnderParChallenge')}>
              <Translate id="navbar.events.submenu.upc" />
            </DropdownItem>
            {/* <DropdownItem tag={Link} className="not-default" to={getPath(languageCode, 'CompetitionFootJoyMasters')}>
              <Translate id="navbar.events.submenu.fjm" />
            </DropdownItem> */}
            <DropdownItem tag={Link} className="not-default" to={getPath(languageCode, 'Extreme')}>
              <Translate id="navbar.events.submenu.extreme" />
            </DropdownItem>
            {/* <DropdownItem tag={Link} className="not-default" to={getPath(languageCode, 'CompetitionIndoorOpen')}>
              <Translate id="navbar.events.submenu.ido" />
            </DropdownItem> */}
            <DropdownItem tag={Link} className="not-default" to={getPath(languageCode, 'Calendar')}>
              <Translate id="navbar.events.submenu.calendar" />
            </DropdownItem>
            <DropdownItem tag={Link} className="not-default" to={getPath(languageCode, 'Tours')}>
              <Translate id="navbar.events.submenu.tours" />
            </DropdownItem>
            <DropdownItem tag={Link} className="not-default" to={getPath(languageCode, 'OrderOfMerit')}>
              <Translate id="navbar.events.submenu.oom" />
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      )}

      {'no' === languageCode && (
        <UncontrolledDropdown nav inNavbar className="d-lg-flex align-items-center">
          <DropdownToggle nav caret>
            <Translate id="navbar.events.title" />
          </DropdownToggle>
          <DropdownMenu className={`dropdown ${styles.dropdownMenu}`}>
            <DropdownItem tag={Link} className="not-default" to={getPath(languageCode, 'CompetitionGolfamoreTrophy')}>
              <Translate id="navbar.events.submenu.gt" />
            </DropdownItem>
            <DropdownItem tag={Link} className="not-default" to={getPath(languageCode, 'Calendar')}>
              <Translate id="navbar.events.submenu.calendar" />
            </DropdownItem>
            <DropdownItem tag={Link} className="not-default" to={getPath(languageCode, 'OrderOfMerit')}>
              <Translate id="navbar.events.submenu.oom" />
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      )}

      {'dk' === languageCode && (
        <UncontrolledDropdown nav inNavbar className="d-lg-flex align-items-center">
          <DropdownToggle nav caret>
            <Translate id="navbar.events.title" />
          </DropdownToggle>
          <DropdownMenu className={`dropdown ${styles.dropdownMenu}`}>
            <DropdownItem tag={Link} className="not-default" to={getPath(languageCode, 'CompetitionUnderParChallenge')}>
              <Translate id="navbar.events.submenu.upc" />
            </DropdownItem>
            <DropdownItem tag={Link} className="not-default" to={getPath(languageCode, 'Tours')}>
              <Translate id="navbar.events.submenu.tours" />
            </DropdownItem>
            <DropdownItem tag={Link} className="not-default" to={getPath(languageCode, 'Calendar')}>
              <Translate id="navbar.events.submenu.calendar" />
            </DropdownItem>
            <DropdownItem tag={Link} className="not-default" to={getPath(languageCode, 'OrderOfMerit')}>
              <Translate id="navbar.events.submenu.oom" />
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      )}

      {'de' === languageCode && (
        <MyNavItem to={getPath(languageCode, 'Tours')} tag={Link}>
          <Translate id="navbar.events.title" />
        </MyNavItem>
      )}

      <MyNavItem to={getPath(languageCode, 'Plus')} tag={Link}>
        <Translate id="navbar.plus" />
      </MyNavItem>

      {'sv' === languageCode && (
        <MyNavItem to={getPath(languageCode, 'Deals')} tag={Link}>
          <Translate id="navbar.more" />
        </MyNavItem>
      )}
    </Nav>
  );
};

const MenuSecondaryItems = (props) => {
  const languageCode = getLanguage();
  const { state, dispatch } = useContext(Store);

  return (
    <>
      <MyNavItem to={getPath(languageCode, 'GolfClub')} tag={Link} {...props}>
        <Translate id="navbar.for-clubs" />
      </MyNavItem>
      <MyNavItem to={getPath(languageCode, 'Account')} tag={Link} {...props}>
        {state.user?.token ? <Translate id="navbar.my-page" /> : <Translate id="navbar.login" />}
      </MyNavItem>
      {state.user?.token && (
        <NavItem {...props} className={`d-lg-flex align-items-center ${props.className || ''}`}>
          <NavLink
            href={'#'}
            onClick={() => {
              dispatch({ type: 'USER_LOGGED_OUT' });
            }}
          >
            <Translate id="account.logout" />
          </NavLink>
        </NavItem>
      )}
    </>
  );
};
